import { useState } from "react";
import { useBranch, BranchAssignment } from "@/contexts/BranchContext";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ShieldCheck, UserPlus, Trash2, Building2, Users, MapPin,
  Lock, Unlock, Eye, Edit2, Search,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

// Demo users for assignment
const AVAILABLE_USERS = [
  { userId: "u-amit", name: "Amit Patel", email: "amit@abc.com", role: "accountant" },
  { userId: "u-neha", name: "Neha Gupta", email: "neha@abc.com", role: "accountant" },
  { userId: "u-vikram", name: "Vikram Joshi", email: "vikram@abc.com", role: "accountant" },
  { userId: "u-divya", name: "Divya Nair", email: "divya@abc.com", role: "accountant" },
  { userId: "u-ravi", name: "Ravi Mehta", email: "ravi@abc.com", role: "accountant" },
  { userId: "u-anita", name: "Anita Rao", email: "anita@abc.com", role: "accountant" },
  { userId: "u-priya", name: "Priya Sharma", email: "priya@cafirm.com", role: "ca" },
  { userId: "u-suresh", name: "Suresh Iyer", email: "suresh@abc.com", role: "accountant" },
  { userId: "u-kavita", name: "Kavita Das", email: "kavita@abc.com", role: "accountant" },
];

const ROLE_COLORS: Record<string, string> = {
  company_admin: "bg-primary/10 text-primary border-primary/20",
  ca: "bg-purple-500/10 text-purple-600 border-purple-500/20",
  accountant: "bg-blue-500/10 text-blue-600 border-blue-500/20",
};

const BranchPermissionsPage = () => {
  const { branches, assignments, assignUser, removeAssignment, getBranchAssignments, getUserBranches } = useBranch();
  const { role } = useAuth();
  const { toast } = useToast();
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedBranchId, setSelectedBranchId] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [assignRole, setAssignRole] = useState("accountant");

  const isAdmin = role === "company_admin" || role === "super_admin";
  const activeBranches = branches.filter((b) => b.isActive);

  // Unique users from assignments
  const uniqueUsers = Array.from(
    new Map(assignments.filter((a) => a.isActive).map((a) => [a.userId, a])).values()
  );

  const handleAssign = () => {
    if (!selectedBranchId || selectedUsers.length === 0) {
      toast({ title: "Missing selection", description: "Select a branch and at least one user", variant: "destructive" });
      return;
    }
    const branch = branches.find((b) => b.id === selectedBranchId);
    let assigned = 0;
    selectedUsers.forEach((uid) => {
      const existing = assignments.find((a) => a.branchId === selectedBranchId && a.userId === uid && a.isActive);
      if (!existing) {
        const user = AVAILABLE_USERS.find((u) => u.userId === uid);
        if (user) {
          assignUser(selectedBranchId, { userId: uid, userName: user.name, userEmail: user.email, userRole: assignRole });
          assigned++;
        }
      }
    });
    setShowAssignDialog(false);
    setSelectedUsers([]);
    toast({ title: "Users Assigned", description: `${assigned} user(s) assigned to ${branch?.name}` });
  };

  const handleRemove = (assignment: BranchAssignment) => {
    removeAssignment(assignment.id);
    toast({ title: "Access Removed", description: `${assignment.userName} removed from branch` });
  };

  const toggleUser = (uid: string) => {
    setSelectedUsers((prev) => prev.includes(uid) ? prev.filter((u) => u !== uid) : [...prev, uid]);
  };

  const filteredUsers = AVAILABLE_USERS.filter((u) =>
    u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Users already assigned to selected branch
  const branchExistingUsers = selectedBranchId
    ? getBranchAssignments(selectedBranchId).map((a) => a.userId)
    : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-display text-foreground flex items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-primary" /> Branch Permissions
          </h1>
          <p className="text-sm text-muted-foreground">
            Control which users can access each branch • {uniqueUsers.length} users across {activeBranches.length} branches
          </p>
        </div>
        {isAdmin && (
          <Button onClick={() => setShowAssignDialog(true)}>
            <UserPlus className="h-4 w-4 mr-1.5" /> Assign User to Branch
          </Button>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {activeBranches.map((branch) => {
          const count = getBranchAssignments(branch.id).length;
          return (
            <Card key={branch.id} className="border-border/50">
              <CardContent className="pt-5 pb-4">
                <div className="flex items-center justify-between mb-2">
                  <MapPin className="h-4 w-4 text-primary" />
                  {branch.isHeadOffice && <Badge variant="secondary" className="text-[9px]">HO</Badge>}
                </div>
                <p className="font-semibold text-sm text-foreground">{branch.city}</p>
                <p className="text-xs text-muted-foreground">{branch.code}</p>
                <div className="flex items-center gap-1.5 mt-2">
                  <Users className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-sm font-medium text-foreground">{count} users</span>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Tabs defaultValue="by-branch">
        <TabsList>
          <TabsTrigger value="by-branch">By Branch</TabsTrigger>
          <TabsTrigger value="by-user">By User</TabsTrigger>
          <TabsTrigger value="matrix">Permission Matrix</TabsTrigger>
        </TabsList>

        {/* By Branch Tab */}
        <TabsContent value="by-branch" className="space-y-4">
          {activeBranches.map((branch) => {
            const branchAssignments = getBranchAssignments(branch.id);
            return (
              <Card key={branch.id} className="border-border/50">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-primary" />
                      {branch.name}
                      {branch.isHeadOffice && <Badge variant="secondary" className="text-[10px]">Head Office</Badge>}
                    </span>
                    <Badge variant="outline" className="text-xs">{branchAssignments.length} users</Badge>
                  </CardTitle>
                  <CardDescription>{branch.city}{branch.state ? `, ${branch.state}` : ""} • GSTIN: {branch.gstin || "N/A"}</CardDescription>
                </CardHeader>
                <CardContent>
                  {branchAssignments.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>User</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Role</TableHead>
                          <TableHead>Assigned</TableHead>
                          <TableHead>Access</TableHead>
                          {isAdmin && <TableHead className="text-right">Actions</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {branchAssignments.map((a) => (
                          <TableRow key={a.id}>
                            <TableCell className="font-medium">{a.userName}</TableCell>
                            <TableCell className="text-muted-foreground text-sm">{a.userEmail}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className={`text-[10px] capitalize ${ROLE_COLORS[a.userRole] || ""}`}>
                                {a.userRole === "ca" ? "CA" : a.userRole}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {new Date(a.assignedAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1.5">
                                {a.isActive ? (
                                  <><Unlock className="h-3.5 w-3.5 text-emerald-500" /><span className="text-xs text-emerald-600">Active</span></>
                                ) : (
                                  <><Lock className="h-3.5 w-3.5 text-destructive" /><span className="text-xs text-destructive">Revoked</span></>
                                )}
                              </div>
                            </TableCell>
                            {isAdmin && (
                              <TableCell className="text-right">
                                <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleRemove(a)}>
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">No users assigned to this branch yet</p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>

        {/* By User Tab */}
        <TabsContent value="by-user" className="space-y-4">
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="text-base">User Branch Access</CardTitle>
              <CardDescription>View which branches each user can access</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Assigned Branches</TableHead>
                    <TableHead>Access Level</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {/* Company Admin - all branches */}
                  <TableRow className="bg-primary/5">
                    <TableCell>
                      <div>
                        <span className="font-medium">Rajesh Kumar</span>
                        <p className="text-xs text-muted-foreground">rajesh@abc.com</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={`text-[10px] ${ROLE_COLORS.company_admin}`}>Company Admin</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1.5">
                        {activeBranches.map((b) => (
                          <Badge key={b.id} variant="outline" className="text-[10px]">{b.city}</Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 text-[10px]">
                        <Eye className="h-3 w-3 mr-1" /> Full Access (All Branches)
                      </Badge>
                    </TableCell>
                  </TableRow>
                  {/* Regular users */}
                  {uniqueUsers.map((u) => {
                    const userBranches = getUserBranches(u.userId);
                    return (
                      <TableRow key={u.userId}>
                        <TableCell>
                          <div>
                            <span className="font-medium">{u.userName}</span>
                            <p className="text-xs text-muted-foreground">{u.userEmail}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`text-[10px] capitalize ${ROLE_COLORS[u.userRole] || ""}`}>
                            {u.userRole === "ca" ? "CA" : u.userRole}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1.5">
                            {userBranches.map((b) => (
                              <Badge key={b.id} variant="outline" className="text-[10px]">{b.city}</Badge>
                            ))}
                            {userBranches.length === 0 && (
                              <span className="text-xs text-muted-foreground">No branches</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-[10px]">
                            <Lock className="h-3 w-3 mr-1" /> {userBranches.length} Branch(es) Only
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Permission Matrix Tab */}
        <TabsContent value="matrix">
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-primary" /> Access Matrix
              </CardTitle>
              <CardDescription>Visual overview of user-branch access permissions</CardDescription>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[180px]">User</TableHead>
                    <TableHead className="min-w-[60px]">Role</TableHead>
                    {activeBranches.map((b) => (
                      <TableHead key={b.id} className="text-center min-w-[100px]">
                        <div className="flex flex-col items-center gap-0.5">
                          <span className="text-xs">{b.city}</span>
                          <span className="text-[9px] text-muted-foreground">{b.code}</span>
                        </div>
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {/* Admin row */}
                  <TableRow className="bg-primary/5">
                    <TableCell className="font-medium">Rajesh Kumar</TableCell>
                    <TableCell><Badge className={`text-[9px] ${ROLE_COLORS.company_admin}`}>Admin</Badge></TableCell>
                    {activeBranches.map((b) => (
                      <TableCell key={b.id} className="text-center">
                        <div className="flex justify-center">
                          <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center">
                            <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
                          </div>
                        </div>
                      </TableCell>
                    ))}
                  </TableRow>
                  {/* User rows */}
                  {uniqueUsers.map((u) => (
                    <TableRow key={u.userId}>
                      <TableCell>
                        <div>
                          <span className="font-medium text-sm">{u.userName}</span>
                          <p className="text-[10px] text-muted-foreground">{u.userEmail}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-[9px] capitalize ${ROLE_COLORS[u.userRole] || ""}`}>
                          {u.userRole === "ca" ? "CA" : u.userRole.slice(0, 4)}
                        </Badge>
                      </TableCell>
                      {activeBranches.map((b) => {
                        const hasAccess = assignments.some((a) => a.userId === u.userId && a.branchId === b.id && a.isActive);
                        return (
                          <TableCell key={b.id} className="text-center">
                            <div className="flex justify-center">
                              {hasAccess ? (
                                <div className="w-6 h-6 rounded-full bg-emerald-500/15 flex items-center justify-center">
                                  <Unlock className="h-3.5 w-3.5 text-emerald-600" />
                                </div>
                              ) : (
                                <div className="w-6 h-6 rounded-full bg-muted/50 flex items-center justify-center">
                                  <Lock className="h-3.5 w-3.5 text-muted-foreground/40" />
                                </div>
                              )}
                            </div>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Assign Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-primary" /> Assign Users to Branch
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Select Branch</Label>
              <Select value={selectedBranchId} onValueChange={setSelectedBranchId}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a branch..." />
                </SelectTrigger>
                <SelectContent>
                  {activeBranches.map((b) => (
                    <SelectItem key={b.id} value={b.id}>{b.name} ({b.city})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>Assign as Role</Label>
              <Select value={assignRole} onValueChange={setAssignRole}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="accountant">Accountant</SelectItem>
                  <SelectItem value="ca">Chartered Accountant</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>Select Users</Label>
              <div className="relative">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
              <div className="max-h-48 overflow-y-auto border border-border rounded-lg divide-y divide-border/50">
                {filteredUsers.map((u) => {
                  const alreadyAssigned = branchExistingUsers.includes(u.userId);
                  return (
                    <label
                      key={u.userId}
                      className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-muted/30 transition-colors ${alreadyAssigned ? "opacity-50" : ""}`}
                    >
                      <Checkbox
                        checked={selectedUsers.includes(u.userId)}
                        onCheckedChange={() => !alreadyAssigned && toggleUser(u.userId)}
                        disabled={alreadyAssigned}
                      />
                      <div className="flex-1">
                        <span className="text-sm font-medium">{u.name}</span>
                        <p className="text-xs text-muted-foreground">{u.email}</p>
                      </div>
                      {alreadyAssigned && (
                        <Badge variant="outline" className="text-[9px]">Already assigned</Badge>
                      )}
                    </label>
                  );
                })}
              </div>
              {selectedUsers.length > 0 && (
                <p className="text-xs text-muted-foreground">{selectedUsers.length} user(s) selected</p>
              )}
            </div>

            <Button onClick={handleAssign} className="w-full" disabled={!selectedBranchId || selectedUsers.length === 0}>
              Assign {selectedUsers.length} User(s) to Branch
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BranchPermissionsPage;
